import type { RagTurn } from "./rag-chain.js";

/**
 * One row in the shape commonly used with Databricks / MLflow GenAI evaluation
 * tables (request / response / retrieved_context). This is an **export** for
 * Mosaic Agent Evaluation and notebooks — not a reimplementation of judges.
 *
 * @see https://docs.databricks.com/aws/en/agents/agent-evaluation/
 */
export interface LegacyAgentEvaluationRecord {
  request: string;
  response: string;
  retrieved_context: Array<{ doc_uri?: string; content: string }>;
  /** Optional expected answer for offline scoring. */
  expected_response?: string;
  /** Free-form tags for filtering in MLflow / notebooks. */
  tags?: Record<string, string>;
  /** Latency and model metadata for cost/latency analysis. */
  metrics?: {
    retrieve_ms?: number;
    generate_ms?: number;
    total_ms?: number;
    input_tokens?: number;
    output_tokens?: number;
  };
}

/** MLflow 3 evaluation-dataset row with structured inputs, outputs, and expectations. */
export interface Mlflow3RagEvaluationRecord {
  inputs: { question: string };
  outputs: {
    answer: string;
    retrieved_context: Array<{ doc_uri?: string; content: string }>;
  };
  expectations?: {
    expected_response?: string;
    expected_retrieved_context?: Array<{ doc_uri?: string; content: string }>;
  };
  tags?: Record<string, string>;
  metadata: {
    model?: string;
    index?: string;
    retrieve_ms: number;
    generate_ms: number;
    total_ms: number;
    input_tokens?: number;
    output_tokens?: number;
    trace_id?: string;
    submission_id?: string;
  };
}

export interface RagEvalExpected {
  /** Substrings that should appear in the answer. */
  mustContain?: string[];
  /** Retrieved chunk ids that should appear among results. */
  mustRetrieveIds?: string[];
  /** Require at least one citation id on the turn. */
  requireCitations?: boolean;
  /** Full expected answer (exact or for Agent Evaluation expected_response). */
  expectedAnswer?: string;
}

/**
 * Convert a {@link RagTurn} into a Databricks-friendly evaluation record.
 * Upload JSONL of these rows to MLflow / Agent Evaluation rather than building
 * a parallel judge stack in Fabric.
 */
export function toAgentEvaluationRecord(
  turn: RagTurn,
  options: { expectedAnswer?: string; tags?: Record<string, string> } = {},
): LegacyAgentEvaluationRecord {
  return {
    request: turn.question,
    response: turn.answer,
    retrieved_context: turn.chunks.map((chunk) => ({
      ...(chunk.id ? { doc_uri: chunk.id } : {}),
      content: chunk.text,
    })),
    ...(options.expectedAnswer !== undefined ? { expected_response: options.expectedAnswer } : {}),
    ...(options.tags ? { tags: options.tags } : {}),
    metrics: {
      retrieve_ms: turn.latencyMs.retrieve,
      generate_ms: turn.latencyMs.generate,
      total_ms: turn.latencyMs.total,
      ...(turn.usage?.inputTokens !== undefined ? { input_tokens: turn.usage.inputTokens } : {}),
      ...(turn.usage?.outputTokens !== undefined ? { output_tokens: turn.usage.outputTokens } : {}),
    },
  };
}

/** Serialize evaluation records as JSONL for MLflow / Agent Evaluation import. */
export function exportAgentEvaluationJsonl(records: LegacyAgentEvaluationRecord[]): string {
  return `${records.map((record) => JSON.stringify(record)).join("\n")}\n`;
}

/** Convert a RAG turn into the structured MLflow 3 evaluation-dataset shape. */
export function toMlflow3EvaluationRecord(
  turn: RagTurn,
  options: {
    expectedAnswer?: string;
    expectedRetrievedContext?: Array<{ doc_uri?: string; content: string }>;
    tags?: Record<string, string>;
    traceId?: string;
    submissionId?: string;
  } = {},
): Mlflow3RagEvaluationRecord {
  const retrievedContext = turn.chunks.map((chunk) => ({
    ...(chunk.id ? { doc_uri: chunk.id } : {}),
    content: chunk.text,
  }));
  const hasExpectations =
    options.expectedAnswer !== undefined || options.expectedRetrievedContext !== undefined;
  return {
    inputs: { question: turn.question },
    outputs: { answer: turn.answer, retrieved_context: retrievedContext },
    ...(hasExpectations
      ? {
          expectations: {
            ...(options.expectedAnswer !== undefined
              ? { expected_response: options.expectedAnswer }
              : {}),
            ...(options.expectedRetrievedContext !== undefined
              ? { expected_retrieved_context: options.expectedRetrievedContext }
              : {}),
          },
        }
      : {}),
    ...(options.tags ? { tags: options.tags } : {}),
    metadata: {
      ...(turn.model ? { model: turn.model } : {}),
      ...(turn.index ? { index: turn.index } : {}),
      retrieve_ms: turn.latencyMs.retrieve,
      generate_ms: turn.latencyMs.generate,
      total_ms: turn.latencyMs.total,
      ...(turn.usage?.inputTokens !== undefined ? { input_tokens: turn.usage.inputTokens } : {}),
      ...(turn.usage?.outputTokens !== undefined ? { output_tokens: turn.usage.outputTokens } : {}),
      ...(options.traceId ? { trace_id: options.traceId } : {}),
      ...(options.submissionId ? { submission_id: options.submissionId } : {}),
    },
  };
}

/** Serialize structured MLflow 3 evaluation rows as newline-delimited JSON. */
export function exportMlflow3EvaluationJsonl(records: Mlflow3RagEvaluationRecord[]): string {
  return `${records.map((record) => JSON.stringify(record)).join("\n")}\n`;
}

export interface RagTurnScore {
  name: string;
  score: number;
  passed: boolean;
  reason: string;
}

/** Lightweight local checks before/alongside Databricks Agent Evaluation. */
export function scoreRagTurn(turn: RagTurn, expected: RagEvalExpected = {}): RagTurnScore[] {
  const scores: RagTurnScore[] = [];

  if (expected.mustContain?.length) {
    const missing = expected.mustContain.filter((needle) => !turn.answer.includes(needle));
    const passed = missing.length === 0;
    scores.push({
      name: "answer_contains",
      score: passed ? 1 : Math.max(0, 1 - missing.length / expected.mustContain.length),
      passed,
      reason: passed ? "Answer contained required facts." : `Missing: ${missing.join(", ")}`,
    });
  }

  if (expected.mustRetrieveIds?.length) {
    const ids = new Set(turn.chunks.map((chunk) => chunk.id).filter(Boolean));
    const missing = expected.mustRetrieveIds.filter((id) => !ids.has(id));
    const passed = missing.length === 0;
    scores.push({
      name: "retrieval_ids",
      score: passed ? 1 : Math.max(0, 1 - missing.length / expected.mustRetrieveIds.length),
      passed,
      reason: passed ? "Retrieved expected document ids." : `Missing ids: ${missing.join(", ")}`,
    });
  }

  if (expected.requireCitations) {
    const passed = turn.citations.length > 0;
    scores.push({
      name: "citations_present",
      score: passed ? 1 : 0,
      passed,
      reason: passed ? `Cited ${turn.citations.length} source(s).` : "No citations on turn.",
    });
  }

  if (expected.expectedAnswer !== undefined) {
    const passed = turn.answer.trim() === expected.expectedAnswer.trim();
    scores.push({
      name: "exact_answer",
      score: passed ? 1 : 0,
      passed,
      reason: passed ? "Answer matched expected." : "Answer did not match expected.",
    });
  }

  // Always report retrieval emptiness as a diagnostic signal.
  scores.push({
    name: "retrieval_nonempty",
    score: turn.chunks.length > 0 ? 1 : 0,
    passed: turn.chunks.length > 0,
    reason:
      turn.chunks.length > 0
        ? `Retrieved ${turn.chunks.length} chunk(s).`
        : "AI Search returned no chunks — check index, query, and UC grants.",
  });

  return scores;
}

/**
 * Adapter for `@fabric-harness/evals` scorers when the suite output is a {@link RagTurn}.
 * Keep heavy quality judges in Databricks Agent Evaluation; use these for CI smoke.
 */
export function ragTurnContainsScorer(name = "rag_answer_contains"): (input: {
  case: { expected?: RagEvalExpected | string };
  output: RagTurn;
}) => RagTurnScore {
  return ({ case: evalCase, output }) => {
    const expected =
      typeof evalCase.expected === "string"
        ? { mustContain: [evalCase.expected] }
        : (evalCase.expected ?? {});
    const scores = scoreRagTurn(output, expected);
    const score = scores.find((candidate) => candidate.name === "answer_contains");
    return score
      ? { ...score, name }
      : {
          name,
          score: 1,
          passed: true,
          reason: "No mustContain expectations.",
        };
  };
}

export function ragTurnCitationsScorer(name = "rag_citations"): (input: {
  output: RagTurn;
}) => RagTurnScore {
  return ({ output }) => {
    const score = scoreRagTurn(output, { requireCitations: true }).find(
      (candidate) => candidate.name === "citations_present",
    );
    return score
      ? { ...score, name }
      : { name, score: 0, passed: false, reason: "Missing citation score." };
  };
}
